import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { downloadCSV, downloadXLSX } from '@/lib/exports';
import { Plus, Edit3, Trash2, Check, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = axios.create({ withCredentials: true });

const BLANK = { name: '', pillar: '', sub_name: '', description: '', price_paise: '', is_open: true };
const inr = (paise) => paise == null ? '' : `₹${(paise / 100).toLocaleString('en-IN')}`;

const AdminCategoriesTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | object
  const [draft, setDraft] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await ax.get(`${API}/admin/categories`); setItems(data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to load categories.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const s = search.toLowerCase();
    return items.filter((c) => [c.name, c.pillar, c.sub_name, c.description].some((v) => String(v || '').toLowerCase().includes(s)));
  }, [items, search]);

  const openNew = () => { setDraft({ ...BLANK }); setEditing('new'); };
  const openEdit = (c) => { setDraft({ ...BLANK, ...c, price_paise: c.price_paise ?? '' }); setEditing(c); };

  const save = async () => {
    if (!draft.name?.trim()) { toast.error('Name is required.'); return; }
    setSaving(true);
    const payload = { ...draft, price_paise: draft.price_paise === '' ? null : parseInt(draft.price_paise) };
    try {
      if (editing === 'new') await ax.post(`${API}/admin/categories`, payload);
      else await ax.patch(`${API}/admin/categories/${editing.id}`, payload);
      toast.success('Saved.'); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed.'); }
    finally { setSaving(false); }
  };

  const toggleOpen = async (c) => {
    try {
      await ax.patch(`${API}/admin/categories/${c.id}`, { is_open: !c.is_open });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed.'); }
  };

  const doDelete = async () => {
    setSaving(true);
    try { await ax.delete(`${API}/admin/categories/${deleteId}`); toast.success('Removed.'); setDeleteId(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Delete failed.'); }
    finally { setSaving(false); }
  };

  const exportRows = filtered.map((c) => ({
    Category: c.name, Pillar: c.pillar, Sub: c.sub_name,
    Description: c.description, 'Price (INR)': c.price_paise ? c.price_paise / 100 : 'Default',
    'Open': c.is_open ? 'Yes' : 'No', 'Total Entries': c.entry_count, 'Submitted': c.submitted_count,
  }));

  return (
    <div data-testid="admin-categories-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <p className="text-[var(--ink-muted)] text-sm">Manage award categories, descriptions, pricing and availability.</p>
        <div className="flex items-center gap-3">
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search categories…" className="bg-[var(--bg-alt)] border border-[var(--gold-faint)] focus:border-[var(--gold)] outline-none px-3 py-2 text-[var(--ink)] text-sm w-56" />
          <button onClick={() => downloadCSV(`adaura-categories-${new Date().toISOString().slice(0,10)}.csv`, exportRows)} className="border border-[var(--gold)] text-[var(--gold)] hover:bg-[var(--gold)] hover:text-[var(--bg)] text-[10px] tracking-[0.3em] uppercase px-4 py-2 transition-colors">CSV</button>
          <button onClick={() => downloadXLSX(`adaura-categories-${new Date().toISOString().slice(0,10)}.xlsx`, exportRows, 'Categories')} className="border border-[var(--gold)] text-[var(--gold)] hover:bg-[var(--gold)] hover:text-[var(--bg)] text-[10px] tracking-[0.3em] uppercase px-4 py-2 transition-colors">Excel</button>
          <button onClick={openNew} data-testid="cat-new-btn" className="inline-flex items-center gap-2 bg-[var(--gold)] hover:bg-[var(--gold-bright)] text-[var(--bg)] text-[10px] tracking-[0.3em] uppercase px-5 py-3 transition-colors"><Plus size={12} /> Add Category</button>
        </div>
      </div>

      {loading ? <div className="py-16 text-center text-[var(--ink-muted)] tracking-[0.3em] uppercase text-xs">Loading…</div> :
       filtered.length === 0 ? <div className="py-16 border border-dashed border-[var(--gold-faint)] text-center text-[var(--ink-muted)]">No categories.</div> :
       <div className="overflow-x-auto border border-[var(--gold-faint)]">
         <table className="w-full text-sm">
           <thead className="bg-[var(--bg-deep)]/50">
             <tr>{['Pillar', 'Sub-Category', 'Price', 'Status', 'Entries', 'Submitted', 'Actions'].map((h) => (
               <th key={h} className="text-left px-4 py-3 text-[10px] tracking-[0.25em] uppercase text-[var(--ink-muted)] font-normal">{h}</th>
             ))}</tr>
           </thead>
           <tbody>
             {filtered.map((c, i) => (
               <tr key={c.id} className="border-t border-[var(--gold-faint)]/40 hover:bg-[var(--gold-faint)]/10" data-testid={`cat-row-${i}`}>
                 <td className="px-4 py-3 text-[var(--ink-muted)]">{c.pillar || '—'}</td>
                 <td className="px-4 py-3 text-[var(--ink)]">{c.sub_name || c.name}</td>
                 <td className="px-4 py-3 text-[var(--gold)]">{c.price_paise ? inr(c.price_paise) : <span className="text-[var(--ink-muted)] italic">Default</span>}</td>
                 <td className="px-4 py-3">
                   <button onClick={() => toggleOpen(c)} data-testid={`cat-toggle-${i}`} className={`text-[10px] tracking-[0.2em] uppercase border px-2 py-1 transition-colors ${c.is_open ? 'border-[#7BC47F] text-[#7BC47F]' : 'border-[var(--terracotta)] text-[var(--terracotta)]'}`}>{c.is_open ? 'Open' : 'Closed'}</button>
                 </td>
                 <td className="px-4 py-3 text-[var(--ink-muted)]">{c.entry_count || 0}</td>
                 <td className="px-4 py-3 text-[#7BC47F]">{c.submitted_count || 0}</td>
                 <td className="px-4 py-3 text-right whitespace-nowrap">
                   <button onClick={() => openEdit(c)} data-testid={`cat-edit-${i}`} className="text-[var(--ink)] hover:text-[var(--gold)] text-xs tracking-[0.2em] uppercase mr-3">Edit</button>
                   <button onClick={() => setDeleteId(c.id)} data-testid={`cat-delete-${i}`} className="text-[var(--terracotta)] text-xs tracking-[0.2em] uppercase">Delete</button>
                 </td>
               </tr>
             ))}
           </tbody>
         </table>
       </div>}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setEditing(null)} />
          <div className="relative bg-[var(--bg-alt)] border border-[var(--gold-faint)] max-w-xl w-full max-h-[88vh] overflow-y-auto">
            <div className="sticky top-0 bg-[var(--bg-alt)] border-b border-[var(--gold-faint)] px-8 py-5 flex items-center justify-between">
              <h2 className="font-[Libre_Baskerville] text-2xl text-[var(--ink)]">{editing === 'new' ? 'Add Category' : 'Edit Category'}</h2>
              <button onClick={() => setEditing(null)} className="text-[var(--ink-muted)] hover:text-[var(--gold)] text-2xl leading-none">×</button>
            </div>
            <div className="p-8 space-y-5">
              <Field label="Pillar (e.g. Brand & Storytelling)" value={draft.pillar} onChange={(v) => setDraft((d) => ({ ...d, pillar: v }))} tid="cat-edit-pillar" />
              <Field label="Sub-Category (e.g. Brand Reinvention)" value={draft.sub_name} onChange={(v) => setDraft((d) => ({ ...d, sub_name: v, name: d.pillar ? `${d.pillar} - ${v}` : v }))} tid="cat-edit-sub" />
              <Field label="Full Name *" value={draft.name} onChange={(v) => setDraft((d) => ({ ...d, name: v }))} tid="cat-edit-name" />
              <div>
                <label className="block text-[10px] tracking-[0.3em] uppercase text-[var(--ink-muted)] mb-2">Description</label>
                <textarea rows={3} value={draft.description} onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))} data-testid="cat-edit-desc" className="w-full bg-[var(--bg)] border border-[var(--gold-faint)] focus:border-[var(--gold)] outline-none px-3 py-2 text-[var(--ink)] text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-5">
                <Field label="Price Override (₹ paise, blank = default ₹7,500)" type="number" value={draft.price_paise} onChange={(v) => setDraft((d) => ({ ...d, price_paise: v }))} tid="cat-edit-price" placeholder="e.g. 1000000 = ₹10,000" />
                <div>
                  <label className="block text-[10px] tracking-[0.3em] uppercase text-[var(--ink-muted)] mb-2">Status</label>
                  <button type="button" onClick={() => setDraft((d) => ({ ...d, is_open: !d.is_open }))} data-testid="cat-edit-open" className={`w-full border px-3 py-2 text-sm ${draft.is_open ? 'border-[#7BC47F] text-[#7BC47F]' : 'border-[var(--terracotta)] text-[var(--terracotta)]'}`}>{draft.is_open ? 'Open' : 'Closed'} — click to toggle</button>
                </div>
              </div>
            </div>
            <div className="sticky bottom-0 bg-[var(--bg-alt)] border-t border-[var(--gold-faint)] px-8 py-4 flex justify-end gap-3">
              <button onClick={() => setEditing(null)} className="text-[var(--ink-muted)] text-xs tracking-[0.2em] uppercase px-4 py-2">Cancel</button>
              <button onClick={save} disabled={saving} data-testid="cat-edit-save" className="bg-[var(--gold)] hover:bg-[var(--gold-bright)] text-[var(--bg)] text-xs tracking-[0.2em] uppercase px-6 py-2 disabled:opacity-50">{saving ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}

      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setDeleteId(null)} />
          <div className="relative bg-[var(--bg-alt)] border border-[var(--terracotta-soft)] max-w-md w-full p-8">
            <h3 className="font-[Libre_Baskerville] text-xl text-[var(--ink)] mb-2">Remove category?</h3>
            <p className="text-[var(--ink-muted)] text-sm">This cannot be undone. If entries exist in this category, deletion will be blocked — close the category instead.</p>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setDeleteId(null)} className="text-[var(--ink-muted)] text-xs tracking-[0.2em] uppercase px-4 py-2">Cancel</button>
              <button onClick={doDelete} disabled={saving} data-testid="cat-delete-confirm" className="bg-[var(--terracotta)] hover:opacity-90 text-white text-xs tracking-[0.2em] uppercase px-6 py-2 disabled:opacity-50">{saving ? 'Removing…' : 'Remove'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, value, onChange, type = 'text', tid, placeholder }) => (
  <div>
    <label className="block text-[10px] tracking-[0.3em] uppercase text-[var(--ink-muted)] mb-2">{label}</label>
    <input data-testid={tid} type={type} value={value ?? ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="w-full bg-[var(--bg)] border border-[var(--gold-faint)] focus:border-[var(--gold)] outline-none px-3 py-2 text-[var(--ink)] text-sm" />
  </div>
);

export default AdminCategoriesTab;
